/**
 * Carte de visite numérique — François CERNEJESKI, Directeur Général.
 *
 * Standalone single-page presentation card in the BBr brand system :
 * cream backdrop, black/gold typography, gold rule accents.
 * Actions : click-to-call, click-to-mail, WhatsApp, save contact (.vcf).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Download, ArrowRight, MapPin, Globe } from "lucide-react";

const DG = {
  fullName: "François CERNEJESKI",
  firstName: "François",
  lastName: "CERNEJESKI",
  role: "Directeur Général",
  organization: "Boulay Beach Resort",
  organizationShort: "BBr",
  email: "francois.cernejeski@boulaybeachresort.com",
  phone: "+225 07 05 49 99 99",
  address: "Île Boulay · Abidjan · Côte d'Ivoire",
  website: "https://workflow-boulaybeachresort.com",
};

function buildVCard() {
  const tel = DG.phone.replace(/\s+/g, "");
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${DG.fullName}`,
    `N:${DG.lastName};${DG.firstName};;;`,
    `ORG:${DG.organization}`,
    `TITLE:${DG.role}`,
    `TEL;TYPE=CELL,VOICE:${tel}`,
    `EMAIL;TYPE=INTERNET,WORK:${DG.email}`,
    `ADR;TYPE=WORK:;;${DG.address};;;`,
    `URL:${DG.website}`,
    "END:VCARD",
  ];
  return lines.join("\r\n");
}

export default function CarteDG() {
  const vcardHref = useMemo(() => {
    const blob = new Blob([buildVCard()], { type: "text/vcard" });
    return URL.createObjectURL(blob);
  }, []);
  const phoneTel = DG.phone.replace(/\s+/g, "");
  const whatsappLink = `https://wa.me/${DG.phone.replace(/\D+/g, "")}`;
  const initials = `${DG.firstName[0]}${DG.lastName[0]}`;

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#0A0A0A]" data-testid="carte-dg">
      {/* Subtle texture backdrop */}
      <div
        className="fixed inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #B8922A 0.5px, transparent 0.5px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Card viewport */}
      <div className="relative flex items-center justify-center min-h-screen px-4 sm:px-6 py-12 md:py-16">
        <article
          className="relative w-full max-w-2xl bg-white shadow-[0_30px_80px_-40px_rgba(10,10,10,0.35)] border border-[#0A0A0A]/8"
          data-testid="carte-dg-card"
        >
          {/* Gold hairline top */}
          <div className="h-1 bg-gradient-to-r from-[#B8922A] via-[#D4B256] to-[#B8922A]" />

          {/* Brand header — official BBr logo */}
          <header className="px-8 sm:px-12 pt-10 pb-6 border-b border-[#0A0A0A]/8 flex items-center justify-center">
            <Link to="/" aria-label="Boulay Beach Resort — Accueil" data-testid="carte-dg-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/b26hlnm6_AA_Plan%20de%20travail%201.png"
                alt="Boulay Beach Resort"
                className="h-14 md:h-16 w-auto"
              />
            </Link>
          </header>

          {/* Identity block */}
          <section className="px-8 sm:px-12 pt-10 md:pt-12">
            <div className="flex items-start gap-6 md:gap-8">
              {/* Avatar — initials monogram (photo-ready placeholder) */}
              <div
                className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 border border-[#B8922A] flex items-center justify-center bg-white"
                aria-hidden="true"
                data-testid="carte-dg-monogram"
              >
                <span className="font-display-serif text-2xl md:text-3xl text-[#B8922A] tracking-widest">
                  {initials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-3">
                  Direction Générale
                </div>
                <h1
                  className="font-display-serif text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.05] text-[#0A0A0A] tracking-tight break-words"
                  data-testid="carte-dg-name"
                >
                  {DG.fullName}
                </h1>
                <div className="h-px w-14 bg-[#D4B256] mt-4 mb-3" />
                <p className="text-[0.9rem] md:text-base text-[#0A0A0A]/70 uppercase tracking-[0.28em]">
                  {DG.role}
                </p>
              </div>
            </div>
          </section>

          {/* Contact block */}
          <section className="px-8 sm:px-12 py-10 md:py-12" aria-label="Coordonnées">
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-5">
              Coordonnées
            </div>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${phoneTel}`}
                  className="group flex items-center gap-4 py-2 border-b border-[#0A0A0A]/6 hover:border-[#B8922A] transition-colors"
                  data-testid="carte-dg-phone"
                >
                  <span className="w-9 h-9 flex items-center justify-center border border-[#0A0A0A]/12 group-hover:border-[#B8922A] group-hover:bg-[#B8922A]/5 transition-colors">
                    <Phone size={14} className="text-[#B8922A]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.58rem] tracking-[0.24em] uppercase text-[#0A0A0A]/45">
                      Téléphone
                    </div>
                    <div className="text-base md:text-lg font-medium tabular-nums text-[#0A0A0A] group-hover:text-[#B8922A] transition-colors">
                      {DG.phone}
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className="text-[#0A0A0A]/25 group-hover:text-[#B8922A] group-hover:translate-x-1 transition-all"
                  />
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${DG.email}`}
                  className="group flex items-center gap-4 py-2 border-b border-[#0A0A0A]/6 hover:border-[#B8922A] transition-colors"
                  data-testid="carte-dg-email"
                >
                  <span className="w-9 h-9 flex items-center justify-center border border-[#0A0A0A]/12 group-hover:border-[#B8922A] group-hover:bg-[#B8922A]/5 transition-colors">
                    <Mail size={14} className="text-[#B8922A]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.58rem] tracking-[0.24em] uppercase text-[#0A0A0A]/45">
                      E-mail
                    </div>
                    <div className="text-sm md:text-[0.98rem] font-medium text-[#0A0A0A] group-hover:text-[#B8922A] transition-colors break-all">
                      {DG.email}
                    </div>
                  </div>
                  <ArrowRight
                    size={14}
                    className="text-[#0A0A0A]/25 group-hover:text-[#B8922A] group-hover:translate-x-1 transition-all flex-shrink-0"
                  />
                </a>
              </li>
              <li className="flex items-center gap-4 py-2">
                <span className="w-9 h-9 flex items-center justify-center border border-[#0A0A0A]/12">
                  <MapPin size={14} className="text-[#B8922A]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.58rem] tracking-[0.24em] uppercase text-[#0A0A0A]/45">
                    Adresse
                  </div>
                  <div className="text-sm md:text-[0.98rem] text-[#0A0A0A]">
                    {DG.address}
                  </div>
                </div>
              </li>
            </ul>
          </section>

          {/* Actions */}
          <section
            className="px-8 sm:px-12 pb-6 md:pb-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
            aria-label="Actions rapides"
          >
            <a
              href={vcardHref}
              download={`${DG.firstName}-${DG.lastName}.vcf`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#B8922A] hover:bg-[#a37e1f] text-white text-[0.68rem] uppercase tracking-[0.28em] transition-colors"
              data-testid="carte-dg-download-vcf"
            >
              <Download size={13} /> Enregistrer le contact
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[0.68rem] uppercase tracking-[0.28em] transition-colors"
              data-testid="carte-dg-whatsapp"
            >
              <MessageCircle size={13} /> WhatsApp
            </a>
          </section>

          {/* Website CTA — full-width, primary access to the resort site */}
          <section className="px-8 sm:px-12 pb-10 md:pb-12">
            <a
              href={DG.website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-3 px-5 py-4 bg-[#0A0A0A] hover:bg-[#B8922A] text-white text-[0.7rem] uppercase tracking-[0.32em] transition-colors group"
              data-testid="carte-dg-website"
            >
              <Globe size={14} />
              Découvrir le resort
              <ArrowRight
                size={13}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </section>

          {/* Footer signature */}
          <footer className="px-8 sm:px-12 py-5 border-t border-[#0A0A0A]/8 bg-[#FAFAF7] flex flex-wrap items-center justify-between gap-3">
            <div className="text-[0.58rem] tracking-[0.32em] uppercase text-[#0A0A0A]/45">
              Carte de visite officielle
            </div>
            <Link
              to="/"
              className="text-[0.6rem] tracking-[0.28em] uppercase text-[#B8922A] hover:underline"
            >
              boulaybeachresort.com
            </Link>
          </footer>

          {/* Gold hairline bottom */}
          <div className="h-1 bg-gradient-to-r from-[#B8922A] via-[#D4B256] to-[#B8922A]" />
        </article>
      </div>
    </div>
  );
}
